// Sell-by-weight (variable weight) shared types + pure helpers. Reused by the
// seller preview grid, the server publish path, and buyer display so the tier
// split and price math never drift between surfaces.

export type SizeTier = "small" | "medium" | "large" | (string & {});

export interface WeightUnit {
  id: string;
  itemId: string;
  productGroupId: string;
  weightCentiLb: number;
  priceCents: number;
  status: "available" | "reserved" | "sold";
}

export interface TierSummary {
  tier: SizeTier;
  label: string;
  count: number;
  weightMin: number;
  weightMax: number;
  priceMin: number;
  priceMax: number;
}

export interface BucketedTier {
  tier: SizeTier;
  min: number;
  max: number;
  indices: number[];
}

const DEFAULT_TIER_KEYS: SizeTier[] = ["small", "medium", "large"];

export function formatWeight(centiLb: number): string {
  return `${(centiLb / 100).toFixed(2)} lb`;
}

export function priceForUnit(weightCentiLb: number, pricePerLbCents: number): number {
  return Math.round((weightCentiLb * pricePerLbCents) / 100);
}

// Split a weight distribution into `tierCount` roughly-even groups (tertiles by
// default), snapping the cut points to natural gaps in the sorted weights when a
// gap sits near the ideal boundary. Pure and deterministic: the seller preview
// and the server publish call this with the same input and get the same split.
export function bucketIntoTiers(
  weightsCentiLb: number[],
  tierCount = 3,
): BucketedTier[] {
  const n = weightsCentiLb.length;
  if (n === 0) return [];

  const order = weightsCentiLb
    .map((w, i) => ({ w, i }))
    .sort((a, b) => a.w - b.w);
  const sorted = order.map((o) => o.w);

  const effectiveCount = Math.max(1, Math.min(tierCount, n));

  // Ideal (even) boundary positions in the sorted array — the index of the
  // first element of each tier after the first.
  const idealCuts: number[] = [];
  for (let k = 1; k < effectiveCount; k++) {
    idealCuts.push(Math.round((k * n) / effectiveCount));
  }

  // Gap sizes between consecutive sorted weights. A boundary between position
  // p-1 and p has gap sorted[p] - sorted[p-1].
  const snapWindow = Math.max(1, Math.floor(n / (effectiveCount * 3)));
  const cuts: number[] = [];
  for (const ideal of idealCuts) {
    let best = ideal;
    let bestGap = -1;
    const lo = Math.max(1, ideal - snapWindow);
    const hi = Math.min(n - 1, ideal + snapWindow);
    for (let p = lo; p <= hi; p++) {
      const gap = sorted[p] - sorted[p - 1];
      if (gap > bestGap) {
        bestGap = gap;
        best = p;
      }
    }
    cuts.push(best);
  }

  // Ensure strictly increasing, unique boundaries.
  const bounds = [0, ...cuts, n];
  for (let k = 1; k < bounds.length; k++) {
    if (bounds[k] <= bounds[k - 1]) bounds[k] = bounds[k - 1] + 1;
  }
  bounds[bounds.length - 1] = n;

  const tiers: BucketedTier[] = [];
  for (let k = 0; k < effectiveCount; k++) {
    const start = bounds[k];
    const end = bounds[k + 1];
    if (start >= end) continue;
    const group = order.slice(start, end);
    const key = tierKeyFor(k, effectiveCount);
    tiers.push({
      tier: key,
      min: sorted[start],
      max: sorted[end - 1],
      indices: group.map((g) => g.i),
    });
  }
  return tiers;
}

function tierKeyFor(index: number, count: number): SizeTier {
  if (count === 3) return DEFAULT_TIER_KEYS[index];
  if (count === 1) return "medium";
  if (count === 2) return index === 0 ? "small" : "large";
  return `tier${index + 1}`;
}

// A sellable unit shows either its seller-typed label ("Per dozen") or its
// weight. Price-option units carry a label and weightCentiLb 0; weighed units
// carry no label. Every surface that renders one unit goes through this so the
// two modes can never diverge.
export function unitDisplayLabel(
  unit: { label?: string | null; weightCentiLb?: number | null } | null | undefined,
): string {
  const label = String(unit?.label ?? "").trim();
  if (label) return label;
  return formatWeight(Number(unit?.weightCentiLb ?? 0));
}

// Stock for a named price option is stored as one row per unit, because the
// per-row reservation is what prevents overselling. Weighed units are each
// genuinely different (a 3.6 lb bird is not a 4.2 lb bird) so every row is a
// real choice; named units are identical, and showing ten "Per dozen" rows is
// nonsense. Collapse those to one entry per label, keeping a representative
// unit id to reserve and a count of how many are left.
export function collapseUnitChoices<
  T extends { id: string; priceCents: number; label?: string | null },
>(units: T[]): { unit: T; remaining: number }[] {
  const out: { unit: T; remaining: number }[] = [];
  const byKey = new Map<string, { unit: T; remaining: number }>();
  for (const u of units) {
    const label = String(u.label ?? "").trim();
    if (!label) {
      out.push({ unit: u, remaining: 1 });
      continue;
    }
    const key = `${label.toLowerCase()}|${u.priceCents}`;
    const seen = byKey.get(key);
    if (seen) seen.remaining += 1;
    else {
      const entry = { unit: u, remaining: 1 };
      byKey.set(key, entry);
      out.push(entry);
    }
  }
  return out;
}

export function unitsAreNamed(
  units: { label?: string | null }[] | null | undefined,
): boolean {
  return (units ?? []).some((u) => String(u?.label ?? "").trim().length > 0);
}
